import React, { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { RobotCommunicationType } from '@moderepo/mode-apis';
import { getRobotDefinitionByRobotClass } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchRobotById, fetchRobotCatalog, rcDataContext, selectRobotById, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCRobotInfoContainer } from './RCRobotInfoContainer';
import { DeviceBasicInfoContainer } from '../devices/DeviceBasicInfoContainer';
import { DeviceDevInfoContainer } from '../devices/DeviceDevInfoContainer';
import { DeviceKVPairsListingContainer } from '../devices/DeviceKVPairsListingContainer';
import { useGetURLParamEntityIdAsNumber, useGetURLParamEntityIdAsString } from '../../utils/utils';



export const RCRobotInfoPage: React.FC = () => {
    // Get the params from the route or url
    const params = useParams();
    const projectId = Number(params.projectId);

    // homeId is required and it can be embed in of the URL params or queryParams
    const homeId = useGetURLParamEntityIdAsNumber('homeId');
    const userId = useGetURLParamEntityIdAsNumber('useId');   // only available if the agent is on User -> Home -> Robot route

    const robotId = useGetURLParamEntityIdAsString('robotId');

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const selectedRobot = selectRobotById(rcDataState, homeId, robotId);

    const { trans } = useRitzTranslation(project);

    // Check if the selectedRobot is a Gateway-less robot class
    const isGatewayLessRobot = useMemo(() => {
        if (selectedRobot && robotCatalog) {
            const robotDefinition = getRobotDefinitionByRobotClass(robotCatalog, selectedRobot.robotClass);
            return robotDefinition?.modeCommunicationType === RobotCommunicationType.DIRECT;
        }

        return false;
    }, [selectedRobot, robotCatalog]);


    // Fetch Robot catalog on component load if we don't have it yet
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(projectId));
        }
    }, [robotCatalog, projectId, rcDataDispatch]);


    // Fetch Robot info on component load if we don't have it yet
    useEffect(() => {
        if (!selectedRobot && homeId && robotId) {
            rcDataDispatch(fetchRobotById(projectId, homeId, robotId));
        }
    }, [selectedRobot, homeId, projectId, robotId, rcDataDispatch]);


    return (
        <div className="page-main-content">
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.robot_info')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.robot_info')}
                    </div>
                </div>
            </div>


            {project && homeId && selectedRobot && (
                <div>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <RCRobotInfoContainer
                                className="match-height"
                                projectId={projectId}
                                homeId={homeId}
                                robotId={robotId}
                            />
                        </Grid>

                        {/*
                            If the selected robot is a gateway-less robot, it means is ia also a device/gateway therefore we need to show the
                            device settings inside Robot Settings page.
                        */}
                        {selectedRobot.deviceId && isGatewayLessRobot && (
                            <>
                                <Grid item xs={12} lg={6}>
                                    <DeviceBasicInfoContainer
                                        className={clsx('match-height')}
                                        projectId={projectId}
                                        deviceId={selectedRobot.deviceId}
                                        userId={userId}
                                        homeId={homeId}
                                        gatewaysOnly
                                    />
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <DeviceDevInfoContainer
                                        className={clsx('match-height')}
                                        projectId={projectId}
                                        deviceId={selectedRobot.deviceId}
                                        userId={userId}
                                        homeId={homeId}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <DeviceKVPairsListingContainer
                                        className={clsx('match-height')}
                                        projectId={projectId}
                                        deviceId={selectedRobot.deviceId}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </div>
            )}

            {(!project || !homeId) && (
                <CircularProgress />
            )}
        </div>
    );
};
